import InformationIconSolid from '@/components/icons/InformationIconSolid';
import { FC } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SettingsFormData } from '@/features/settings/SettingsPage';
import { cn } from '@/lib/utils';
import InformationCircle from '@/components/icons/InformationCircle';
import { Button } from '@/components/ui/button';
import { ColorsHelper } from '@/utils/ColorsHelper';
import EColorPicker from '@/components/everridge/EColorPicker';
import ETextColorPicker from '../../../components/everridge/ETextColorPicker';

type Props = {
	control: Control<SettingsFormData>;
	setValue: UseFormSetValue<SettingsFormData>;
	isMobile?: boolean;
};

const SettingsColor: FC<Props> = ({ control, setValue, isMobile }) => {
	const { t } = useTranslation();

	const colorsPalette = [
		'#2B2B2B',
		'#E06A1C',
		'#E6A82A',
		'#9FF219',
		'#2EB566',
		'#32A4CA',
		'#1C67E0',
	];

	return (
		<div
			className={cn(
				'w-1/2 p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
				isMobile && 'w-full p-0 border-0 bg-popover-30',
			)}>
			<h3 className={cn(isMobile && 'text-base')}>
				{t('main.settings.components.color.title')}
			</h3>
			<div className='w-full flex flex-col gap-1.5 select-none'>
				<h4 className='text-sm font-medium'>
					{t('main.settings.components.color.elements.palette')}
				</h4>
				<Controller
					name={'primaryColor'}
					control={control}
					render={({ field: { value, onChange } }) => {
						const handleSetColor = (color: string) => {
							ColorsHelper.setNewColor(color);
							const darkMode =
								ColorsHelper.getColorContrast(color) === 'black'
									? true
									: false;

							onChange(color);
							setValue('darkMode', darkMode);
						};

						return (
							<div className='flex justify-between gap-1 flex-wrap '>
								{colorsPalette.map((color, index) => (
									<div
										key={index}
										className={cn(
											'size-11 flex items-center justify-center shrink-0 rounded-full cursor-pointer hover:bg-popover-100',
											value === color
												? `border-2`
												: 'border border-popover-100',
										)}
										style={{
											borderColor:
												value === color
													? color
													: undefined,
										}}
										onClick={() => handleSetColor(color)}>
										<div
											className='size-8 rounded-full'
											style={{ backgroundColor: color }}
										/>
									</div>
								))}
							</div>
						);
					}}
				/>
			</div>
			<div className='w-full flex flex-col gap-1.5 select-none'>
				<h4 className='text-sm font-medium'>
					{t('main.settings.components.color.elements.custom')}
				</h4>
				<Controller
					name={'primaryColor'}
					control={control}
					render={({
						field: { value, onChange, onBlur },
						fieldState,
						formState,
					}) => {
						const handleSetColor = () => {
							if (value) {
								ColorsHelper.setNewColor(value);

								const darkMode =
									ColorsHelper.getColorContrast(value) ===
									'black'
										? true
										: false;

								setValue('darkMode', darkMode);
							}
						};

						return (
							<>
								<div className='flex gap-2'>
									<div className='relative w-full'>
										<input
											value={value ?? ''}
											onChange={onChange}
											onBlur={onBlur}
											disabled={formState.disabled}
											placeholder={'e.g. #2B2B2B'}
											className={cn(
												'w-full p-3.5 pr-11 border border-popover-100 rounded-semi-xl text-xs text-popover-800 bg-popover-10 focus:outline-hidden focus:border focus:border-popover-1000 placeholder:text-popover-600 disabled:bg-popover-50 transition-colors duration-150',
												fieldState.invalid
													? 'border-error-1000'
													: '',
											)}
										/>
										<EColorPicker
											value={value}
											onChange={onChange}
										/>
									</div>
									<Button onClick={handleSetColor}>
										{t('elements.buttons.select')}
									</Button>
								</div>
								{fieldState.invalid && (
									<div className='flex items-center gap-1.5'>
										<InformationCircle className='size-4 stroke-error-1000' />
										<span className='text-xs text-error-1000'>
											{fieldState.error?.message}
										</span>
									</div>
								)}
							</>
						);
					}}
				/>
			</div>
			<div className='w-full flex flex-col gap-1.5 select-none'>
				<h4 className='text-sm font-medium'>
					{t('main.settings.components.color.elements.text')}
				</h4>
				<Controller
					name={'darkMode'}
					control={control}
					render={({
						field: { value, onChange, onBlur },
						fieldState,
					}) => {
						const handleChangeMode = (darkMode: boolean) => {
							const contrast = darkMode ? 'black' : 'white';

							onChange(darkMode);
							ColorsHelper.setNewContrast(contrast);
						};

						return (
							<>
								<div className='flex gap-2'>
									<ETextColorPicker
										text={'Light'}
										activated={!value}
										onClick={() => handleChangeMode(false)}
									/>
									<ETextColorPicker
										text={'Dark'}
										activated={value}
										onClick={() => handleChangeMode(true)}
									/>
								</div>
								{fieldState.invalid && (
									<div className='flex items-center gap-1.5'>
										<InformationCircle className='size-4 stroke-error-1000' />
										<span className='text-xs text-error-1000'>
											{fieldState.error?.message}
										</span>
									</div>
								)}
							</>
						);
					}}
				/>
			</div>
			<div className='p-3.5 flex gap-2 border border-popover-100 rounded-semi-xl bg-popover-10'>
				<InformationIconSolid className='size-4 shrink-0' />
				<p className='text-xs font-medium text-popover-800'>
					{t('main.settings.components.color.text')}
				</p>
			</div>
		</div>
	);
};

export default SettingsColor;
